// components/desktop/GoogleReviewsContact.js
'use client';
import { useState, useEffect } from 'react';
import CloudImage from '../CloudImage';
import Icon from '../Icon';
import MediaContainer from '../MediaContainer';
import { LuStar, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { businessInfo as business } from '@/content/globals';

export default function GoogleReviewsContact() {
  const [reviews, setReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(5.0);
  const [totalReviews, setTotalReviews] = useState(270);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch('/api/google-reviews');
        const data = await res.json();
        // Expecting API response: { reviews: [...], rating: 5.00, user_ratings_total: 270 }
        setReviews(data.reviews || []);
        setOverallRating(data.rating || 5.0);
        setTotalReviews(data.user_ratings_total || 270);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    }
    fetchReviews();
  }, []);

  // Function to handle cycling through reviews
  const nextReview = () => {
    if (reviews.length > 0) {
      setCurrentReviewIndex((prevIndex) => (prevIndex + 1) % reviews.length);
    }
  };

  const prevReview = () => {
    if (reviews.length > 0) {
      setCurrentReviewIndex(
        (prevIndex) => (prevIndex - 1 + reviews.length) % reviews.length,
      );
    }
  };

  return (
    <div className="mt-8 rounded-lg bg-gray-50 p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold">Customer Reviews</h3>
        <div className="flex items-center">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Icon
                key={star}
                icon={LuStar}
                size={18}
                className={
                  star <= Math.round(overallRating)
                    ? 'fill-yellow-500 text-yellow-500'
                    : 'text-gray-300'
                }
              />
            ))}
          </div>
          <span className="ml-2 font-medium">{overallRating.toFixed(1)}</span>
          <span className="ml-2 text-gray-600">({totalReviews} reviews)</span>
        </div>
      </div>

      {reviews.length > 0 ? (
        <div className="relative">
          <div className="rounded-lg border border-gray-200 bg-white p-12">
            <p className="mb-4 text-gray-700 italic">
              &quot;{reviews[currentReviewIndex]?.text || ''}&quot;
            </p>
            <div className="mb-2 flex items-center">
              <div className="mr-3 h-10 w-10 rounded-full">
                {reviews[currentReviewIndex]?.profile_photo_url && (
                  <NextImage
                    src={reviews[currentReviewIndex].profile_photo_url}
                    alt={reviews[currentReviewIndex].author_name}
                    width={40}
                    height={40}
                  />
                )}
              </div>
              <div>
                <p className="font-medium">
                  {reviews[currentReviewIndex]?.author_name}
                </p>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Icon
                      key={star}
                      icon={LuStar}
                      size={16}
                      className={
                        star <= (reviews[currentReviewIndex]?.rating || 0)
                          ? 'fill-yellow-500 text-yellow-500'
                          : 'text-gray-300'
                      }
                    />
                  ))}
                </div>
              </div>
            </div>

            {reviews[currentReviewIndex]?.time && (
              <p className="mt-2 text-xs text-gray-500">
                {new Date(
                  reviews[currentReviewIndex].time * 1000,
                ).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="mt-4 flex justify-between text-white">
            <button
              onClick={prevReview}
              className="bg-primary hover:bg-primary/80 rounded-full p-2 transition-colors"
              aria-label="Previous review"
            >
              <Icon icon={LuChevronLeft} size={20} />
            </button>
            <button
              onClick={nextReview}
              className="bg-primary hover:bg-primary/80 rounded-full p-2 transition-colors"
              aria-label="Next review"
            >
              <Icon icon={LuChevronRight} size={20} />
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-gray-700 italic">
            &quot;Absolutely the best detailing in {business.address.city}! My
            car looks brand new.&quot; <br />– <strong>Happy Client</strong>
          </p>
        </div>
      )}

      <NextLink
        href="https://www.google.com/search?q=mayes+auto+detailing#mpd=~857882730039553670/customers/reviews"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary mt-4 inline-block hover:underline"
      >
        Read all our reviews on Google
      </NextLink>
    </div>
  );
}
